// Представление «Посты». Пост принадлежит объекту, поэтому у представления есть
// обязательный контекст — выбранный объект (?f.site=…). Без него это не пустой список,
// а «выберите объект»: пустое, нулевое и неизвестное выглядят по-разному (правило 7).

import type { ReactNode } from 'react';

import { RiBuilding4Line, RiShieldUserLine } from '@remixicon/react';
import { Link } from 'react-router';

import { ListPane } from '@/layout/ListPane';
import { countLabel } from '@/shared/text/plural';
import { parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { formatHours, hoursWord } from '@/shared/hours/format';

import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { лишниеЧасыПоста } from '../post-hours';
import type { SitesRoute } from '../route';
import { FILTER_SITE, sitesHref } from '../route';
import { usePostsQuery, useSiteQuery } from '@/shared/org/queries';

const POST_FORMS = ['пост', 'поста', 'постов'] as const;
const GUARD_FORMS = ['охранник', 'охранника', 'охранников'] as const;

export function PostsList({ route, viewSwitcher }: { route: SitesRoute; viewSwitcher: ReactNode }) {
  const { search, rowId } = route;
  const siteId = search.filters[FILTER_SITE];
  const query = usePostsQuery(siteId);

  if (siteId === undefined) {
    return (
      <ListPane caption='Посты' viewSwitcher={viewSwitcher}>
        <EmptyState
          icon={RiBuilding4Line}
          title='Выберите объект'
          description='Пост принадлежит объекту, и списка «все посты сразу» не бывает: часы одного объекта нельзя складывать с часами другого. Откройте объект в реестре — его посты появятся здесь.'
          action={
            <LinkButton.Root variant='primary' size='medium' underline asChild>
              <Link to={sitesHref('objects', { filters: {}, panels: [] })}>К реестру объектов</Link>
            </LinkButton.Root>
          }
        />
      </ListPane>
    );
  }

  return (
    <ListPane
      caption={query.data === undefined ? 'Посты' : countLabel(query.data.total, POST_FORMS)}
      sliceAt={parseMoment(query.data?.generated_at)}
      viewSwitcher={viewSwitcher}
      note={<SelectedSiteHint siteId={siteId} search={search} />}
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='список постов объекта'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={<ListSkeleton rows={4} />}
        empty={
          <EmptyState
            icon={RiShieldUserLine}
            title='У объекта нет ни одного поста'
            description='Пока постов нет, расчётный норматив часов равен нулю и график смен строить не из чего. Заведите пост в карточке объекта, вкладка «Посты».'
            action={
              <LinkButton.Root variant='primary' size='medium' underline asChild>
                <Link to={`${sitesHref('objects', { ...search, tab: 'posts' }, siteId)}`}>
                  Открыть карточку объекта
                </Link>
              </LinkButton.Root>
            }
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {data.items.map((post) => {
              // Колонка узкая, и фраза целиком тут не встанет: строка несёт короткую метку, а
              // что не так и что сделать, сказано в карточке поста справа (`post-hours.ts`).
              const лишние = лишниеЧасыПоста(post.hours_per_day, post.simultaneous_guards) !== null;
              return (
                <ListRow
                  key={post.id}
                  to={sitesHref('posts', search, post.id)}
                  selected={post.id === rowId}
                  title={post.name}
                  subtitle={[
                    `${formatHours(post.hours_per_day)} ${hoursWord(post.hours_per_day)} в сутки`,
                    countLabel(post.simultaneous_guards, GUARD_FORMS) + ' одновременно',
                  ].join(' · ')}
                  badge={
                    !лишние && post.required_guard_category === null ? undefined : (
                      <span className='flex gap-1'>
                        {/* Вариант с тёмным текстом на оранжевом, а не бледный: оранжевые
                            буквы на почти белом не читаются, а метка здесь — единственное,
                            что говорит о поломке, пока карточка поста не открыта. */}
                        {лишние && (
                          <Badge.Root variant='light' color='orange' size='medium'>
                            часы не сходятся
                          </Badge.Root>
                        )}
                        {post.required_guard_category !== null && (
                          <Badge.Root variant='lighter' color='blue' size='medium'>
                            {post.required_guard_category} разряд
                          </Badge.Root>
                        )}
                      </span>
                    )
                  }
                />
              );
            })}
          </div>
        )}
      </ScreenState>
    </ListPane>
  );
}

/** Какой объект сейчас показан: без этой подписи список постов висит без хозяина. */
function SelectedSiteHint({ siteId, search }: { siteId: string; search: SitesRoute['search'] }) {
  const site = useSiteQuery(siteId);
  return (
    <p className='text-paragraph-xs text-text-sub-600'>
      Объект:{' '}
      <Link
        to={sitesHref('objects', { ...search, filters: {}, tab: 'posts' }, siteId)}
        className='text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
      >
        {site.data?.name ?? 'загружаем название…'}
      </Link>
    </p>
  );
}
